// Erro de digitação no domínio do e-mail (gamil.com, hotmai.com, uol.com).
//
// Endereço com domínio errado não recebe nada: o e-mail volta como hard bounce, e no "esqueci
// minha senha" o Devise (paranoid) nem envia, então quem pediu fica esperando. Aqui só sugerimos
// o domínio provável; quem decide é quem digitou.
//
// Compara o nome do provedor (o primeiro rótulo) e o sufixo separados, como o mailcheck, e só
// para os provedores grandes. Distância de edição sobre o domínio inteiro acertava "seara.com.br"
// em "terra.com.br" e "bemol.com.br" em "bol.com.br", que existem. Nome parecido só conta com a
// mesma primeira letra ("cloud.com" e "ecloud.com.br" são empresas, não iCloud), e o sufixo só é
// trocado quando é um erro conhecido ou com/com.br no provedor errado (uol.com): "terra.cl",
// "ig.ufrj.br" e "gmail.empresa.com.br" existem. Provedor com domínio por país (hotmail.fr,
// yahoo.com.ve) só tem o sufixo corrigido quando ele está na lista de erros conhecidos.
//
// A mesma tabela (email-typo-domains.json) é lida pelo Ruby em Mailing::EmailTypo, que corrige a
// base. Mudou a regra aqui, muda lá: os dois specs cobrem os mesmos casos.
import data from "./email-typo-domains.json";

type Provider = { tlds: string[]; fuzzy?: number; loose?: boolean };

const PROVIDERS: Record<string, Provider> = data.providers;
const VALID_DOMAINS = new Set(data.validDomains);
const NO_FUZZ_LABELS = new Set(data.noFuzzLabels);
const TLD_TYPOS = new Set(data.tldTypos);
const FIXABLE_TLDS = new Set(data.fixableTlds);

// Distância de Damerau-Levenshtein restrita (letras trocadas de lugar contam 1: gamil → gmail).
export function editDistance(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, () => Array<number>(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) d[i][0] = i;
  for (let j = 0; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[a.length][b.length];
}

function providerFor(label: string): string | null {
  if (Object.prototype.hasOwnProperty.call(PROVIDERS, label)) return label;
  if (NO_FUZZ_LABELS.has(label)) return null;

  let best: string | null = null;
  let bestDistance = Infinity;
  for (const [name, provider] of Object.entries(PROVIDERS)) {
    if (!provider.fuzzy || label[0] !== name[0]) continue;
    const distance = editDistance(label, name);
    if (distance <= provider.fuzzy && distance < bestDistance) {
      best = name;
      bestDistance = distance;
    }
  }
  return best;
}

export function suggestDomain(rawDomain: string): string | null {
  const domain = rawDomain.trim().toLowerCase();
  if (VALID_DOMAINS.has(domain)) return null;

  const dot = domain.indexOf(".");
  if (dot <= 0) return null;
  const label = domain.slice(0, dot);
  const tld = domain.slice(dot + 1);
  if (!tld) return null;

  const name = providerFor(label);
  if (!name) return null;

  const provider = PROVIDERS[name];
  let fixedTld = tld;
  if (!provider.tlds.includes(tld)) {
    const fixable = TLD_TYPOS.has(tld) || (!provider.loose && FIXABLE_TLDS.has(tld));
    if (!fixable) return null;
    fixedTld = [...provider.tlds].sort((x, y) => editDistance(tld, x) - editDistance(tld, y))[0];
  }

  const suggestion = `${name}.${fixedTld}`;
  return suggestion === domain ? null : suggestion;
}

export function suggestEmail(rawEmail: string): string | null {
  const email = rawEmail.trim();
  const at = email.lastIndexOf("@");
  if (at <= 0 || at === email.length - 1) return null;

  const domain = suggestDomain(email.slice(at + 1));
  return domain ? `${email.slice(0, at)}@${domain}` : null;
}
